import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  Container, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  CircularProgress,
  Grid,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  Tabs,
  Tab,
  Select,
  InputLabel,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Autocomplete,
  TablePagination,
  InputAdornment
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  PhotoCamera as PhotoCameraIcon,
  CloudUpload as CloudUploadIcon,
  CameraAlt as CameraAltIcon,
  Check as CheckIcon,
  Add as AddIcon,
  Search as SearchIcon
} from '@mui/icons-material';

import { imageURL } from '../../../imageURL'; // Correct relative path
import { resolvePledgePhotoUrl } from '../../../utils/pledgePhotoUrl';
import axios from 'axios';
import baseURL from '../../../baseURL';
import MasterPageShell, {
  masterPaperSx,
  masterListPaperSx,
  masterPrimaryBtnSx,
  masterFieldSx,
  masterTableHeadCellSx,
} from '../../common/MasterPageShell';
import { DHARA_OPTIONS } from '../../../constants/dhara';

import { compressImageToFile } from '../../../utils/compressImage';
import { pickGoldArticleRate } from '../../../utils/articleMasterRate';

// Helper function to compress image to dataURL (for localStorage)
const compressImage = (file, maxWidth = 800, maxHeight = 800, quality = 0.7) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/** Option B: each newline-separated segment is split into chunks of maxLen (no total length limit). */
const ADDRESS_LINE_MAX_CHARS = 30;

function wrapAddressPerLineSegments(value, maxLen = ADDRESS_LINE_MAX_CHARS) {
  if (value == null || value === '') return '';
  return value
    .split('\n')
    .map((segment) => {
      if (segment.length <= maxLen) return segment;
      const parts = [];
      for (let i = 0; i < segment.length; i += maxLen) {
        parts.push(segment.slice(i, i + maxLen));
      }
      return parts.join('\n');
    })
    .join('\n');
}

// Helper function to save to localStorage with error handling
const saveToLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    if (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
      // Clear old photos and try again
      try {
        const existingPhotos = JSON.parse(localStorage.getItem('ledgerPhotos') || '{}');
        // Keep only the most recent photo for each type
        const photoKeys = Object.keys(existingPhotos);
        if (photoKeys.length > 0) {
          // Clear all except the current one being saved
          const photosToKeep = {};
          photoKeys.forEach(key => {
            if (key !== 'ledgerPhotos') {
              // Try to keep only essential photos
              photosToKeep[key] = existingPhotos[key];
            }
          });
          localStorage.setItem('ledgerPhotos', JSON.stringify(photosToKeep));
          // Try saving again
          localStorage.setItem(key, value);
          return true;
        } else {
          // If still fails, clear everything and save
          localStorage.clear();
          localStorage.setItem(key, value);
          return true;
        }
      } catch (e2) {
        console.error('Failed to save to localStorage after cleanup:', e2);
        return false;
      }
    }
    throw e;
  }
};

const PhotoUploadComponent = ({ 
  photoType, 
  onPhotoCapture, 
  onFileUpload,
  existingPhotos,
  compact = false,
  inline = false
}) => {
  // Refs with explicit initialization
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const dialogRef = useRef(null);
  
  // State variables
  const [openCamera, setOpenCamera] = useState(false);
  const [currentStream, setCurrentStream] = useState(null);
  const [isDialogReady, setIsDialogReady] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [photoStatus, setPhotoStatus] = useState({
    captured: false,
    confirmed: false
  });

  // Enhanced camera initialization
  const startCamera = useCallback(async () => {
    // Reset previous states
    setCameraError(null);
    setIsDialogReady(false);

    try {
      // Comprehensive browser support check
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera access not supported in this browser');
      }

      // Prepare constraints with specific requirements
      const constraints = {
        video: {
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 720, max: 1080 },
          facingMode: 'environment'  // Prefer back camera
        }
      };

      // Request camera access
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      // Ensure video ref is set and is a valid DOM element
      if (!videoRef.current || !(videoRef.current instanceof HTMLVideoElement)) {
        console.error('Video reference is not a valid video element');
        throw new Error('Invalid video reference');
      }

      // Set video source and play
      videoRef.current.srcObject = stream;
      
      // Wait for metadata to load
      await new Promise((resolve, reject) => {
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play()
            .then(() => {
              resolve();
              setIsDialogReady(true);
            })
            .catch(reject);
        };
        videoRef.current.onerror = reject;
      });

      // Update state
      setCurrentStream(stream);
      setOpenCamera(true);

    } catch (error) {
      console.error('Camera Initialization Error:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });

      // Detailed error messaging
      let userFriendlyMessage = 'Failed to access camera';
      switch (error.name) {
        case 'NotAllowedError':
          userFriendlyMessage = 'Camera access denied. Please check browser permissions.';
          break;
        case 'NotFoundError':
          userFriendlyMessage = 'No camera found. Please connect a camera.';
          break;
        case 'OverconstrainedError':
          userFriendlyMessage = 'Camera does not meet required specifications.';
          break;
      }

      setCameraError(userFriendlyMessage);
      setOpenCamera(false);
    }
  }, []);

  // Open camera handler with additional checks
  const handleOpenCamera = () => {
    // Ensure refs are properly set
    if (!videoRef.current) {
      console.warn('Creating video reference');
      videoRef.current = document.createElement('video');
      videoRef.current.setAttribute('playsinline', '');
      videoRef.current.setAttribute('autoplay', '');
    }

    if (!canvasRef.current) {
      console.warn('Creating canvas reference');
      canvasRef.current = document.createElement('canvas');
    }

    // Open camera and initialize
    setOpenCamera(true);
    startCamera();
  };

  // Capture photo method
  const capturePhoto = () => {
    try {
      // Ensure video and canvas refs exist
      if (!videoRef.current || !canvasRef.current) {
        console.error('Video or Canvas reference missing');
        return;
      }

      // Get video dimensions
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      // Set canvas to same dimensions as video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw current video frame to canvas
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to data URL (base64 image)
      const capturedImageDataUrl = canvas.toDataURL('image/jpeg');

      // Stop video stream
      if (currentStream) {
        currentStream.getTracks().forEach(track => track.stop());
      }

      // Immediately process and save the image
      fetch(capturedImageDataUrl)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], `${photoType}_capture.jpg`, { type: 'image/jpeg' });
          
          // Compress image for upload (<100KB for cloud) and for localStorage
          Promise.all([
            compressImageToFile(file, photoType === 'clientPhoto' || photoType === 'aadharCard' || photoType === 'panCard' ? 2 : 100, 320, 320, 0.5),
            compressImage(file) // Compress to dataURL for localStorage
          ])
            .then(([compressedFile, compressedDataUrl]) => {
              // Save to localStorage
              const existingPhotos = JSON.parse(localStorage.getItem('ledgerPhotos') || '{}');
              existingPhotos[photoType] = compressedDataUrl;
              const saved = saveToLocalStorage('ledgerPhotos', JSON.stringify(existingPhotos));
              
              if (compressedDataUrl) {
                setPreviewImage(compressedDataUrl);
                setPhotoStatus({
                  captured: true,
                  confirmed: true
                });
              }
              if (!saved) {
                setCameraError('Failed to save photo. Storage quota exceeded. Please clear some photos.');
              }
              
              // Call onFileUpload with compressed file (for server upload)
              if (onFileUpload) {
                onFileUpload(compressedFile, photoType);
              }
            })
            .catch(error => {
              console.error('Error compressing image:', error);
              setCameraError('Failed to process image');
            });
          
          // Close camera dialog
          setOpenCamera(false);
        })
        .catch(error => {
          console.error('Error processing image:', error);
          setCameraError('Failed to process captured image');
        });

    } catch (error) {
      console.error('Photo capture error:', error);
      setCameraError('Failed to capture photo');
    }
  };

  // File upload handler
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const isPdf = file.type === 'application/pdf';
      const maxKb =
        !isPdf && (photoType === 'clientPhoto' || photoType === 'aadharCard' || photoType === 'panCard')
          ? 2
          : 100;
      Promise.all([
        isPdf ? Promise.resolve(file) : compressImageToFile(file, maxKb, 320, 320, 0.5),
        isPdf ? Promise.resolve(null) : compressImage(file)
      ])
        .then(([compressedFile, compressedDataUrl]) => {
          // Save to localStorage
          const existingPhotos = JSON.parse(localStorage.getItem('ledgerPhotos') || '{}');
          existingPhotos[photoType] = compressedDataUrl;
          const saved = saveToLocalStorage('ledgerPhotos', JSON.stringify(existingPhotos));
          
          if (compressedDataUrl) {
            setPreviewImage(compressedDataUrl);
            setPhotoStatus({
              captured: true,
              confirmed: true
            });
          }
          if (!saved) {
            setCameraError('Failed to save photo. Storage quota exceeded. Please clear some photos.');
          }
          
          // Call file upload callback with compressed file (for server upload)
          if (onFileUpload) {
            onFileUpload(compressedFile, photoType);
          }
        })
        .catch(error => {
          console.error('Error compressing image:', error);
          setCameraError('Failed to process image');
        });
    }
  };

  // Effect to load existing photos from localStorage
  useEffect(() => {
    const savedPhotos = JSON.parse(localStorage.getItem('ledgerPhotos') || '{}');
    const stored = savedPhotos[photoType];
    const passed = existingPhotos && existingPhotos[photoType];
    const url = (typeof stored === 'string' && stored)
      ? stored
      : (typeof passed === 'string' && passed)
        ? passed
        : '';
    if (!url) return;
    setPreviewImage(url);
    setPhotoStatus({ captured: true, confirmed: true });
  }, [photoType, existingPhotos]);

  // Reset camera and preview states when photoType changes
  useEffect(() => {
    // Close camera if open
    setOpenCamera(false);
    
    // Clear any previous camera errors
    setCameraError(null);
  }, [photoType]);

  // Reset photo previews across different photo types
  const resetPhotoPreviews = () => {
    // Reset preview images
    setPreviewImage(null);
    
    // Reset photo status
    setPhotoStatus({
      captured: false,
      confirmed: false
    });
  };

  return (
    <Box 
      ref={dialogRef}
      sx={{
        display: 'flex',
        flexDirection: inline ? 'row' : 'column',
        alignItems: 'center',
        justifyContent: inline ? 'flex-start' : 'center',
        gap: inline ? 1 : 0,
        border: inline ? '1px solid #e2e8f0' : '1px dashed',
        borderColor: inline ? '#e2e8f0' : (compact ? 'rgba(201,162,77,0.55)' : 'grey.300'),
        borderRadius: inline ? '10px' : (compact ? 1.5 : 2),
        p: inline ? 0.75 : (compact ? 0.75 : 2),
        px: inline ? 1.25 : undefined,
        textAlign: 'center',
        width: inline ? 'auto' : '100%',
        flex: inline ? '0 1 auto' : undefined,
        position: 'relative',
        minHeight: inline || compact ? 0 : 250,
        background: '#fff'
      }}
    >
      {/* File Input */}
      <input
        ref={fileInputRef}
        accept="image/*"
        style={{ display: 'none' }}
        id={`${photoType}-file-input`}
        type="file"
        onChange={handleFileUpload}
      />
      
      {/* Preview Image or Placeholder */}
      {compact && !inline && (
        <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', color: '#64748b', textTransform: 'uppercase', mb: 0.25 }}>
          {photoType === 'aadharCard' ? 'Aadhar' : photoType === 'panCard' ? 'PAN' : 'Client'}
        </Typography>
      )}
      {previewImage && typeof previewImage === 'string' && (
        <Box sx={{ position: 'relative', width: inline ? 56 : '100%', height: inline ? 56 : 'auto', maxHeight: inline ? 56 : (compact ? 52 : 200), flex: '0 0 auto', borderRadius: inline ? '8px' : 0, overflow: 'visible' }}>
          <img 
            src={previewImage} 
            alt="Preview" 
            style={{ 
              width: inline ? 56 : 'auto',
              height: inline ? 56 : 'auto',
              maxWidth: '100%', 
              maxHeight: inline ? 56 : (compact ? 52 : 200), 
              objectFit: 'cover',
              borderRadius: inline ? 8 : 0,
              display: 'block'
            }} 
            onError={(e) => {
              console.error('Image load error for:', previewImage);
              e.target.onerror = null;
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBkPSJNMTIgMkM2LjQ4IDIgMiA2LjQ4IDIgMTJzNC40OCAxMCAxMCAxMCAxMC00LjQ4IDEwLTEwUzE3LjUyIDIgMTIgMnptMSAxNWgtMnYtMmgydjJ6bTAtNGgtMlY3aDJ2NnoiLz48L3N2Zz4=';
            }} 
          />
          <IconButton
            color="error"
            size="small"
            sx={{ 
              position: 'absolute', 
              top: inline ? -8 : 0, 
              right: inline ? -8 : 0,
              width: inline ? 18 : 28,
              height: inline ? 18 : 28,
              bgcolor: '#fff',
              border: '1px solid #fecaca',
              p: 0
            }}
            onClick={() => {
              const storedPhotos = JSON.parse(localStorage.getItem('ledgerPhotos') || '{}');
              delete storedPhotos[photoType];
              localStorage.setItem('ledgerPhotos', JSON.stringify(storedPhotos));
              setPreviewImage(null);
              setPhotoStatus({
                captured: false,
                confirmed: false
              });
              if (onFileUpload) onFileUpload(null, photoType);
            }}
          >
            <DeleteIcon sx={{ fontSize: inline ? 12 : 18 }} />
          </IconButton>
        </Box>
      )}
      
      {/* Action Buttons */}
      <Box 
        sx={{ 
          display: 'flex', 
          gap: 0.5, 
          mt: inline ? 0 : (compact ? 0.5 : 2), 
          width: inline ? 'auto' : '100%', 
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}
      >
        {/* File Upload Button */}
        <label htmlFor={`${photoType}-file-input`}>
          <Button 
            variant="outlined" 
            component="span" 
            startIcon={<CloudUploadIcon sx={{ fontSize: inline ? 14 : 18 }} />}
            color="primary"
            size="small"
            sx={inline ? { minWidth: 0, height: 28, px: 1, py: 0, fontSize: 11, lineHeight: 1, textTransform: 'none', borderRadius: '8px', '& .MuiButton-startIcon': { mr: 0.4 } } : undefined}
          >
            Upload
          </Button>
        </label>

        <Button 
          variant="outlined" 
          color="secondary" 
          startIcon={<CameraAltIcon sx={{ fontSize: inline ? 14 : 18 }} />}
          onClick={handleOpenCamera}
          size="small"
          sx={inline ? { minWidth: 0, height: 28, px: 1, py: 0, fontSize: 11, lineHeight: 1, textTransform: 'none', borderRadius: '8px', '& .MuiButton-startIcon': { mr: 0.4 } } : undefined}
        >
          Capture
        </Button>
      </Box>

      {/* Camera Capture Dialog */}
      <Dialog 
        open={openCamera} 
        onClose={() => {
          if (currentStream) {
            currentStream.getTracks().forEach(track => track.stop());
          }
          setOpenCamera(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Capture {photoType.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
        </DialogTitle>
        <DialogContent>
          {cameraError ? (
            <Alert severity="error">{cameraError}</Alert>
          ) : (
            <Box 
              sx={{ 
                position: 'relative', 
                width: '100%', 
                paddingTop: '56.25%', // 16:9 aspect ratio
                backgroundColor: 'black' 
              }}
            >
              <video 
                ref={videoRef} 
                playsInline
                autoPlay
                style={{ 
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  backgroundColor: 'black'
                }} 
              />
              <canvas 
                ref={canvasRef} 
                style={{ display: 'none' }} 
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            if (currentStream) {
              currentStream.getTracks().forEach(track => track.stop());
            }
            setOpenCamera(false);
          }}>Cancel</Button>
          <Button 
            onClick={capturePhoto} 
            disabled={!!cameraError}
          >
            Capture
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`photo-tabpanel-${index}`}
      aria-labelledby={`photo-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function placeContains(options, inputValue) {
  const q = String(inputValue || '').trim().toUpperCase();
  return options.filter((name) => !q || String(name).toUpperCase().includes(q));
}

function PlaceSearchField({ id, label, required, value, options, allowAddNew, onPick, onAddNew }) {
  return (
    <Autocomplete
      id={id}
      fullWidth
      openOnFocus
      autoHighlight
      options={allowAddNew ? [...options, '__ADD_NEW__'] : options}
      value={value || null}
      filterOptions={(opts, state) => {
        const filtered = placeContains(opts.filter((o) => o !== '__ADD_NEW__'), state.inputValue);
        return allowAddNew ? [...filtered, '__ADD_NEW__'] : filtered;
      }}
      getOptionLabel={(opt) => (opt === '__ADD_NEW__' ? 'Add New Village' : opt || '')}
      isOptionEqualToValue={(opt, val) => opt === val}
      onChange={(_e, val) => {
        if (val === '__ADD_NEW__') {
          onAddNew?.();
          return;
        }
        onPick(val || '');
      }}
      renderOption={(props, option) => (
        option === '__ADD_NEW__' ? (
          <li {...props} key="__ADD_NEW__" style={{ color: '#1e3a5f', fontWeight: 700 }}>
            <AddIcon sx={{ mr: 1, fontSize: 'small' }} />
            Add New Village
          </li>
        ) : (
          <li {...props} key={option}>{option}</li>
        )
      )}
      renderInput={(params) => (
        <TextField {...params} label={label} required={required} margin="normal" placeholder="Type to search" />
      )}
      ListboxProps={{ style: { maxHeight: 280 } }}
      componentsProps={{ popper: { sx: { zIndex: 100000 } } }}
    />
  );
}

const LedgerMaster = ({ isModal, onCustomerCreated, onClose, prefill }) => {
  // Ensure body scroll is restored when entering Ledger (avoids stuck state from other screens)
  useEffect(() => {
    document.body.style.overflow = 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  const [idTypes, setIdTypes] = useState([]);
  const [selectedIdTypes, setSelectedIdTypes] = useState([]);
  const [modalIdPicks, setModalIdPicks] = useState([]);
  const [showOtherFields, setShowOtherFields] = useState(false);
  const [otherIdName, setOtherIdName] = useState('');
  const [otherIdNumber, setOtherIdNumber] = useState('');
  const [idNumbers, setIdNumbers] = useState({
    aadhar: '',
    pan: '',
    voter: '',
    driving: ''
  });

  // Add New Caste Modal State
  const [addCasteModalOpen, setAddCasteModalOpen] = useState(false);
  const [newCasteForm, setNewCasteForm] = useState({ name: '' });
  const [casteLoading, setCasteLoading] = useState(false);

  // Add New Village Modal State
  const [addVillageModalOpen, setAddVillageModalOpen] = useState(false);
  const [newVillageForm, setNewVillageForm] = useState({
    village: '',
    atPost: '',
    taluka: '',
    district: ''
  });
  
  // Ensure form values are never undefined
  const resetVillageForm = () => {
    setNewVillageForm({
      village: '',
      atPost: '',
      taluka: '',
      district: ''
    });
  };
  
  // Add effect to track modal state changes
  useEffect(() => {
    console.log('🏠 Village Modal State Changed:', addVillageModalOpen);
    if (addVillageModalOpen) {
      console.log('🚀 Village Modal Opened');
      console.log('📝 Current newVillageForm:', newVillageForm);
    } else {
      console.log('🚪 Village Modal Closed');
    }
  }, [addVillageModalOpen, newVillageForm]);
  
  // Add effect to track newVillageForm changes
  useEffect(() => {
    console.log('📝 newVillageForm updated:', newVillageForm);
  }, [newVillageForm]);
  const [villageLoading, setVillageLoading] = useState(false);



  const idTypeOptions = [
    { value: 'aadhar', label: 'Aadhar Card' },
    { value: 'pan', label: 'Pancard' },
    { value: 'voter', label: 'Voter ID' },
    { value: 'driving', label: 'Driving Licence' },
    { value: 'other', label: 'Other' }
  ];

  const handleIdTypeChange = (event) => {
    const selectedValues = event.target.value;
    setSelectedIdTypes(selectedValues);
    setShowOtherFields(selectedValues.includes('other'));
  };

  const MODAL_ID_OPTIONS = [
    { value: 'client', label: 'Client Photo', photo: 'clientPhoto' },
    { value: 'aadhar', label: 'Aadhar Card', photo: 'aadharCard', numberKey: 'aadhar', numberLabel: 'Aadhar Number' },
    { value: 'pan', label: 'PAN Card', photo: 'panCard', numberKey: 'pan', numberLabel: 'PAN Number' },
    { value: 'driving', label: 'Driving Licence', numberKey: 'driving', numberLabel: 'DL Number' },
    { value: 'voter', label: 'Voter ID', numberKey: 'voter', numberLabel: 'Voter ID Number' },
    { value: 'other', label: 'Other' }
  ];

  const handleModalIdPicks = (event) => {
    const raw = event.target.value;
    const next = typeof raw === 'string' ? raw.split(',') : raw;
    setModalIdPicks(next);
    setSelectedIdTypes(next.filter((value) => value !== 'client'));
    setShowOtherFields(next.includes('other'));
  };

  const handleOtherIdNameChange = (event) => {
    setOtherIdName(event.target.value);
  };

  const handleOtherIdNumberChange = (event) => {
    setOtherIdNumber(event.target.value);
  };

  const handleIdNumberChange = (field) => (event) => {
    const newIdNumbers = { ...idNumbers };
    newIdNumbers[field] = event.target.value;
    setIdNumbers(newIdNumbers);
  };

  // Add New Caste Handlers
  const handleAddCaste = async () => {
    if (!newCasteForm.name.trim()) {
      setCreateError('Caste name is required');
      return;
    }

    setCasteLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${baseURL}/castes`, 
        { name: newCasteForm.name },
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );

      // Add new caste to the list (response.data.data contains the created caste)
      setCastes(prev => [response.data.data, ...prev]);
      
      // Select the newly created caste
      setLedgerForm(prev => ({
        ...prev,
        caste: response.data.data.name
      }));

      // Close modal and reset form
      setAddCasteModalOpen(false);
      setNewCasteForm({ name: '' });
      setCreateSuccess(true);
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Failed to create caste');
    } finally {
      setCasteLoading(false);
    }
  };

  // Add New Village Handlers
  const handleAddVillage = async () => {
    console.log('🚀 handleAddVillage called');
    console.log('📝 newVillageForm data:', newVillageForm);
    
    setVillageLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${baseURL}/town-villages`, 
        {
          village: newVillageForm.village,
          atPost: newVillageForm.atPost,
          taluka: newVillageForm.taluka,
          district: newVillageForm.district
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      // Add new village to the list (response.data.data contains the created village)
      setTownVillages(prev => [response.data.data, ...prev]);
      
      // Select the newly created village and auto-fill other fields (like caste)
      setLedgerForm(prev => ({
        ...prev,
        village: response.data.data.village,
        atPost: response.data.data.atPost,
        taluka: response.data.data.taluka,
        district: response.data.data.district
      }));

      // Close modal and reset form
      setAddVillageModalOpen(false);
      resetVillageForm();
      setCreateSuccess(true);
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Failed to create village');
    } finally {
      setVillageLoading(false);
    }
  };

  

  const [ledgers, setLedgers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Ledger list pagination
  const [ledgerPage, setLedgerPage] = useState(0);
  const [ledgerRowsPerPage, setLedgerRowsPerPage] = useState(50);
  const [ledgerTotalDocs, setLedgerTotalDocs] = useState(0);
  const [ledgerSearch, setLedgerSearch] = useState('');

  // Ledger Form State
  const initialLedgerForm = {
    _id: '',
    type: 'Customer',
    firstName: '',
    lastName: '',
    relation: '',
    relationName: '',
    address: '',
    caste: '',
    occupation: '',
    mobileNo: '',
    village: '',
    atPost: '',
    taluka: '',
    district: '',
    panCardNo: '',
    aadharCardNo: '',
    groupCode: '',
    openingBalanceType: '',
    openingBalance: '',
    rateOfInterest: '',
    dhara: '30_day',
    photos: {
      aadharCard: null,
      panCard: null,
      clientPhoto: null
    }
  };
  
  // Ensure form values are never undefined
  const resetLedgerForm = () => {
    setLedgerForm(initialLedgerForm);
  };

  const [ledgerForm, setLedgerForm] = useState(() => ({
    ...initialLedgerForm,
    firstName: prefill?.firstName || '',
    lastName: prefill?.lastName || '',
    mobileNo: prefill?.mobileNo || ''
  }));
  const [isEditing, setIsEditing] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(false);
  const [createError, setCreateError] = useState(null);

  // Delete Ledger State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ledgerToDelete, setLedgerToDelete] = useState(null);

  // Dropdown Data
  const [castes, setCastes] = useState([]);
  const [townVillages, setTownVillages] = useState([]);
  const [groupCodes, setGroupCodes] = useState([]);

  const [photoUploadTab, setPhotoUploadTab] = useState(0);

  // Unique occupation suggestions from existing ledgers (Customer type)
  const occupationSuggestions = React.useMemo(() => {
    const list = (ledgers || [])
      .filter((l) => l.occupation && String(l.occupation).trim())
      .map((l) => String(l.occupation).trim());
    return [...new Set(list)].sort();
  }, [ledgers]);

  const sortedCastes = useMemo(
    () =>
      [...castes].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base', numeric: true })
      ),
    [castes]
  );

  const sortedGroupCodes = useMemo(
    () =>
      [...groupCodes].sort((a, b) => {
        const byName = (a.name || '').localeCompare(b.name || '', undefined, {
          sensitivity: 'base',
          numeric: true,
        });
        if (byName !== 0) return byName;
        return String(a.code || '').localeCompare(String(b.code || ''), undefined, {
          sensitivity: 'base',
          numeric: true,
        });
      }),
    [groupCodes]
  );

  const uniqueVillagesSorted = useMemo(
    () =>
      [...new Set(townVillages.map((tv) => (tv.village || '').trim()).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      ),
    [townVillages]
  );

  const uniqueAtPostsSorted = useMemo(
    () =>
      [...new Set(townVillages.map((tv) => (tv.atPost || '').trim()).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      ),
    [townVillages]
  );

  const uniqueTalukasSorted = useMemo(
    () =>
      [...new Set(townVillages.map((tv) => (tv.taluka || '').trim()).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      ),
    [townVillages]
  );

  const uniqueDistrictsSorted = useMemo(
    () =>
      [...new Set(townVillages.map((tv) => (tv.district || '').trim()).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      ),
    [townVillages]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('Starting to fetch data');
        const token = localStorage.getItem('token');
        console.log('Token:', token);
        
        const [ledgersResponse, castesResponse, townVillagesResponse, groupCodesResponse] = await Promise.all([
          axios.get(`${baseURL}/ledgers`, { params: { page: 1, limit: ledgerRowsPerPage }, headers: { 'Authorization': `Bearer ${token}` } }),
          axios.get(`${baseURL}/castes`, { params: { limit: 10000 }, headers: { 'Authorization': `Bearer ${token}` } }),
          axios.get(`${baseURL}/town-villages`, { 
            params: { limit: 10000 }, // Get ALL villages
            headers: { 'Authorization': `Bearer ${token}` } 
          }),
          axios.get(`${baseURL}/group-codes`, { params: { limit: 10000 }, headers: { 'Authorization': `Bearer ${token}` } })
        ]);

        console.log('Ledgers Response:', ledgersResponse.data);
        console.log('Castes Response:', castesResponse.data);
        console.log('Town Villages Response:', townVillagesResponse.data);
        console.log('Group Codes Response:', groupCodesResponse.data);

        // Update to handle different response structures
        const ledgerRes = ledgersResponse.data;
        setLedgers(ledgerRes.data || ledgerRes);
        setLedgerTotalDocs(ledgerRes.totalDocs ?? (ledgerRes.data || ledgerRes).length);
        setLedgerPage(0);
        
        // Handle castes response
        const casteData = castesResponse.data.data || castesResponse.data;
        setCastes(Array.isArray(casteData) ? casteData : []);
        
        // Handle town villages response
        const townVillageData = townVillagesResponse.data.data || townVillagesResponse.data;
        setTownVillages(Array.isArray(townVillageData) ? townVillageData : []);
        
        // Handle group codes response
        const groupCodeData = groupCodesResponse.data.data || groupCodesResponse.data;
        setGroupCodes(Array.isArray(groupCodeData) ? groupCodeData : []);
        
        setLoading(false);
      } catch (err) {
        console.error('Fetch Data Error:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status,
          headers: err.config?.headers
        });
        setError(`Failed to fetch data: ${err.message}`);
        setLoading(false);
      }
    };

    console.log('Calling fetchData');
    fetchData();
  }, []);

  const fetchLedgersPage = async (page = 0, rowsPerPage = ledgerRowsPerPage, search = ledgerSearch) => {
    try {
      const token = localStorage.getItem('token');
      const params = { page: page + 1, limit: rowsPerPage };
      const q = String(search ?? '').trim();
      if (q) params.search = q;
      const response = await axios.get(`${baseURL}/ledgers`, {
        params,
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const res = response.data;
      setLedgers(res.data || res);
      setLedgerTotalDocs(res.totalDocs ?? (res.data || res).length);
      setLedgerPage(page);
      setLedgerRowsPerPage(rowsPerPage);
    } catch (err) {
      console.error('Fetch ledgers page error:', err);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchLedgersPage(0, ledgerRowsPerPage, ledgerSearch);
    }, 350);
    return () => clearTimeout(timer);
  }, [ledgerSearch]);

  const handleLedgerPageChange = (event, newPage) => {
    fetchLedgersPage(newPage, ledgerRowsPerPage, ledgerSearch);
  };

  const handleLedgerRowsPerPageChange = (event) => {
    const newRows = parseInt(event.target.value, 10);
    setLedgerRowsPerPage(newRows);
    fetchLedgersPage(0, newRows, ledgerSearch);
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      // Remove ledger-related photos from localStorage
      localStorage.removeItem('ledgerPhotos');
    };

    // Add event listener for page refresh/close
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup event listener
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (ledgerForm.village) {
      const matchingTownVillage = townVillages.find(tv => 
        tv.village && tv.village.trim().toUpperCase() === ledgerForm.village.trim().toUpperCase()
      );
      
      if (matchingTownVillage) {
        setLedgerForm(prev => ({
          ...prev,
          atPost: matchingTownVillage.atPost,
          taluka: matchingTownVillage.taluka,
          district: matchingTownVillage.district
        }));
      }
    }
  }, [ledgerForm.village, townVillages]);

  useEffect(() => {
    if (ledgerForm.type !== 'Customer' || isEditing) return;
    const token = localStorage.getItem('token');
    axios
      .get(`${baseURL}/articles`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const articles = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
        const rate = pickGoldArticleRate(articles);
        if (rate == null) return;
        setLedgerForm((prev) => {
          if (prev.type !== 'Customer' || prev._id) return prev;
          const empty = prev.rateOfInterest === '' || prev.rateOfInterest == null;
          if (!empty) return prev;
          return { ...prev, rateOfInterest: String(rate) };
        });
      })
      .catch(() => {});
  }, [ledgerForm.type, isEditing]);



  const handleInputChange = (e) => {
    const { name, value, files, type } = e.target;
    
    if (type === 'file') {
      setLedgerForm(prev => ({
        ...prev,
        photos: {
          ...prev.photos,
          [name]: files[0] || null
        }
      }));
    } else {
      // Handle "Add New" selections
      if (value === '__ADD_NEW__') {
        if (name === 'caste') {
          setAddCasteModalOpen(true);
        } else if (name === 'village') {
          console.log('🚀 Opening Village Modal');
          console.log('📝 Current form state:', ledgerForm);
          setAddVillageModalOpen(true);
        }
        return;
      }
      
      // Don't uppercase select/number fields — values must stay exact
      if (
        name === 'type' ||
        name === 'openingBalanceType' ||
        name === 'dhara' ||
        name === 'rateOfInterest' ||
        name === 'mobileNo'
      ) {
        setLedgerForm((prev) => ({
          ...prev,
          [name]: value,
          ...(name === 'type' && value === 'Customer' && !prev._id
            ? { dhara: prev.dhara || '30_day' }
            : {})
        }));
        return;
      }
      
      const textValue =
        name === 'address' ? wrapAddressPerLineSegments(value) : value;
      const upperFields = new Set([
        'firstName',
        'lastName',
        'address',
        'occupation',
        'village',
        'atPost',
        'taluka',
        'district'
      ]);
      const finalValue = upperFields.has(name) ? String(textValue).toUpperCase() : textValue;
      setLedgerForm((prev) => ({
        ...prev,
        [name]: finalValue
      }));
    }
  };

  const validateForm = () => {
    console.log('Validation disabled - form will always pass');
    return true;
  };

  const handleCreateLedger = async (e) => {
    e.preventDefault();
    console.log('Form submission started');
    setCreateLoading(true);
    setCreateError(null);

    console.log('Form data:', ledgerForm);
    console.log('Selected ID types:', selectedIdTypes);
    console.log('ID numbers:', idNumbers);

    // Validation disabled - proceed with form submission
    console.log('Proceeding with form submission (no validation)');

    try {
      const token = localStorage.getItem('token');
      console.log('Token:', token ? 'Present' : 'Missing');

      // ----- BUILD COMMON PAYLOAD (without photos) -----
      const basePayload = {};
      Object.keys(ledgerForm).forEach(key => {
        if (key === '_id' || key === 'photos') return;
        let value = ledgerForm[key];
        // Enum fields: never send "" — backend expects null / Debit / Credit
        if (key === 'openingBalanceType') {
          basePayload[key] = value === 'Debit' || value === 'Credit' ? value : null;
          return;
        }
        if (key === 'dhara') {
          basePayload[key] = value ? value : null;
          return;
        }
        if (key === 'rateOfInterest' || key === 'openingBalance') {
          if (value === '' || value == null) {
            basePayload[key] = key === 'openingBalance' ? 0 : null;
          } else {
            basePayload[key] = value;
          }
          return;
        }
        basePayload[key] = value == null ? '' : value;
      });

      // ID numbers from idNumbers / otherId fields
      if (selectedIdTypes.includes('aadhar')) {
        basePayload.aadharCardNo = String(idNumbers.aadhar ?? '');
      }
      if (selectedIdTypes.includes('pan')) {
        basePayload.panCardNo = String(idNumbers.pan ?? '');
      }
      if (selectedIdTypes.includes('voter')) {
        basePayload.voterIdNo = String(idNumbers.voter ?? '');
      }
      if (selectedIdTypes.includes('driving')) {
        basePayload.drivingLicenceNo = String(idNumbers.driving ?? '');
      }
      if (selectedIdTypes.includes('other')) {
        basePayload.otherIdName = String(otherIdName ?? '');
        basePayload.otherIdNumber = String(otherIdNumber ?? '');
      }

      // ----- EDIT MODE: PUT /ledgers/:id (multipart so photos can update) -----
      if (isEditing && ledgerForm._id) {
        console.log('Updating existing ledger:', ledgerForm._id, basePayload);

        const formData = new FormData();
        Object.keys(basePayload).forEach((key) => {
          let value = basePayload[key];
          if (value === null || value === undefined) value = '';
          formData.append(key, value);
        });

        const isUploadablePhoto = (v) =>
          !!v && typeof v !== 'string' && (v instanceof File || (typeof Blob !== 'undefined' && v instanceof Blob));

        if (ledgerForm.photos) {
          Object.keys(ledgerForm.photos).forEach((photoType) => {
            const photo = ledgerForm.photos[photoType];
            if (isUploadablePhoto(photo)) {
              formData.append(photoType, photo);
              console.log(`Added photo for update ${photoType}:`, photo.name || photoType);
            }
          });
        }

        const response = await axios.put(
          `${baseURL}/ledgers/${ledgerForm._id}`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        const updatedRaw = response.data?.data || response.data;
        const updated = {
          ...updatedRaw,
          relation: updatedRaw?.relation || ledgerForm.relation || '',
          relationName: updatedRaw?.relationName || ledgerForm.relationName || ''
        };
        if (updated?.code && (updated.relation || updated.relationName)) {
          try {
            const map = JSON.parse(localStorage.getItem('girviCustomerRelation') || '{}');
            map[updated.code] = {
              relation: updated.relation || '',
              relationName: updated.relationName || ''
            };
            localStorage.setItem('girviCustomerRelation', JSON.stringify(map));
          } catch {
            /* ignore storage errors */
          }
        }
        console.log('Update response:', updated);

        // Update ledger list in-place
        setLedgers(prev =>
          prev.map(l => (l._id === updated._id ? { ...l, ...updated } : l))
        );

        setIsEditing(false);
        resetForm();
        setCreateSuccess(true);
        setCreateLoading(false);
        return;
      }

      // ----- CREATE MODE: POST /ledgers with multipart (supports photos) -----
      const formData = new FormData();

      // Basic fields
      Object.keys(basePayload).forEach(key => {
        let value = basePayload[key];
        if (value === null || value === undefined) value = '';
        formData.append(key, value);
        console.log(`Added field ${key}:`, value);
      });

      // Photos if present
      if (ledgerForm.photos) {
        Object.keys(ledgerForm.photos).forEach(photoType => {
          if (ledgerForm.photos[photoType]) {
            formData.append(photoType, ledgerForm.photos[photoType]);
            console.log(`Added photo ${photoType}:`, ledgerForm.photos[photoType]);
          }
        });
      }

      console.log('Making API call to:', `${baseURL}/ledgers`);
      const response = await axios.post(`${baseURL}/ledgers`, formData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const rawCreated = response.data?.data || response.data;
      const newCustomer = {
        ...rawCreated,
        relation: rawCreated?.relation || ledgerForm.relation || '',
        relationName: rawCreated?.relationName || ledgerForm.relationName || ''
      };
      if (newCustomer?.code && (newCustomer.relation || newCustomer.relationName)) {
        try {
          const map = JSON.parse(localStorage.getItem('girviCustomerRelation') || '{}');
          map[newCustomer.code] = {
            relation: newCustomer.relation || '',
            relationName: newCustomer.relationName || ''
          };
          localStorage.setItem('girviCustomerRelation', JSON.stringify(map));
        } catch {
          /* ignore storage errors */
        }
      }
      console.log('API response:', newCustomer);
      
      // Call the callback to close modal and update parent (only for customer creation in pledge flow)
      if (onCustomerCreated) {
        console.log('Calling onCustomerCreated callback');
        onCustomerCreated(newCustomer);
      }
      
      if (onClose) {
        console.log('Calling onClose callback');
        onClose();
      }
      
      setCreateSuccess(true);
      setCreateLoading(false);
      
      // Reset form after successful creation
      resetForm();
      console.log('Form reset completed');
      
      // Refresh the ledger list (current page)
      const refreshResponse = await axios.get(`${baseURL}/ledgers`, { 
        params: {
          page: ledgerPage + 1,
          limit: ledgerRowsPerPage,
          ...(ledgerSearch.trim() ? { search: ledgerSearch.trim() } : {})
        },
        headers: { 'Authorization': `Bearer ${token}` } 
      });
      const res = refreshResponse.data;
      setLedgers(res.data || res);
      setLedgerTotalDocs(res.totalDocs ?? (res.data || res).length);
    } catch (err) {
      console.error('Create/Update Ledger Error:', err.response?.data);
      console.error('Error details:', err);
      setCreateError(err.response?.data?.message || 'Failed to save ledger');
      setCreateLoading(false);
    }
  };

  const handleEditLedger = async (ledger) => {
    console.log('Ledger object:', ledger); // Inspect the ledger object
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${baseURL}/ledgers/${ledger._id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const ledgerData = response.data;
      console.log('Received ledger data:', ledgerData); // Debug log

      // Create URLs for the photos
      const createPhotoUrl = (filename) => {
        const url = resolvePledgePhotoUrl(filename);
        if (url) console.log('Created URL:', url);
        return url;
      };

      const photoUrls = {
        aadharCard: createPhotoUrl(ledgerData.photos?.aadharCard),
        panCard: createPhotoUrl(ledgerData.photos?.panCard),
        clientPhoto: createPhotoUrl(ledgerData.photos?.clientPhoto)
      };

      console.log('Photo URLs:', photoUrls); // Debug log

      // Test image URLs
      Object.entries(photoUrls).forEach(([key, url]) => {
        if (url) {
          fetch(url)
            .then(response => {
              if (!response.ok) {
                console.error(`Failed to load ${key} image:`, response.status);
              }
            })
            .catch(error => console.error(`Error loading ${key} image:`, error));
        }
      });

      setLedgerForm({
        _id: ledgerData._id,
        type: ledgerData.type,
        firstName: ledgerData.firstName,
        lastName: ledgerData.lastName,
        relation: ledgerData.relation || '',
        relationName: ledgerData.relationName || '',
        address: wrapAddressPerLineSegments(
          (ledgerData.address ?? '').toString()
        ),
        caste: ledgerData.caste,
        occupation: ledgerData.occupation,
        mobileNo: ledgerData.mobileNo,
        village: ledgerData.village,
        atPost: ledgerData.atPost,
        taluka: ledgerData.taluka,
        district: ledgerData.district,
        panCardNo: ledgerData.panCardNo,
        aadharCardNo: ledgerData.aadharCardNo,
        groupCode: ledgerData.groupCode,
        openingBalanceType: ledgerData.openingBalanceType || '',
        openingBalance: ledgerData.openingBalance || '',
        rateOfInterest: ledgerData.rateOfInterest ?? '',
        dhara: ledgerData.dhara || '30_day',
        photos: photoUrls
      });

      // Store photo URLs in localStorage
      localStorage.setItem('ledgerPhotos', JSON.stringify(photoUrls));
      setIsEditing(true);
    } catch (error) {
      console.error('Error fetching ledger details:', error);
      setCreateError('Failed to load ledger details');
    }
  };

  const handleDeleteLedger = async () => {
    if (!ledgerToDelete) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${baseURL}/ledgers/${ledgerToDelete._id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setDeleteDialogOpen(false);
      setLedgerToDelete(null);
      setCreateSuccess(true);
      fetchLedgersPage(ledgerPage, ledgerRowsPerPage, ledgerSearch);
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Failed to delete ledger');
    }
  };

  const handleCloseSnackbar = () => {
    setCreateSuccess(false);
    setCreateError(null);
  };

  const handlePhotoCapture = (file, photoType) => {
    setLedgerForm(prev => ({
      ...prev,
      photos: {
        ...prev.photos,
        [photoType]: file
      }
    }));
  };

  const handleFileUpload = (file, photoType) => {
    setLedgerForm(prev => ({
      ...prev,
      photos: {
        ...prev.photos,
        [photoType]: file
      }
    }));
  };

  const resetForm = () => {
    setLedgerForm({
      _id: '',
      type: 'Customer',
      firstName: '',
      lastName: '',
      relation: '',
      relationName: '',
      address: '',
      caste: '',
      occupation: '',
      mobileNo: '',
      village: '',
      atPost: '',
      taluka: '',
      district: '',
      panCardNo: '',
      aadharCardNo: '',
      groupCode: '',
      openingBalanceType: '',
      openingBalance: '',
      rateOfInterest: '',
      dhara: '30_day',
      photos: {
        aadharCard: null,
        panCard: null,
        clientPhoto: null
      }
    });
    setIsEditing(false);
    setSelectedIdTypes([]);
    setIdNumbers({
      aadhar: '',
      pan: '',
      voter: '',
      driving: ''
    });
    setOtherIdName('');
    setOtherIdNumber('');
    setShowOtherFields(false);
    resetPhotoPreviews();
  };

  const resetPhotoPreviews = () => {
    // Reset preview images in form
    setLedgerForm(prev => ({
      ...prev,
      photos: {
        aadharCard: null,
        panCard: null,
        clientPhoto: null
      }
    }));

    // Clear photos from localStorage
    localStorage.removeItem('ledgerPhotos');
  };

  const renderCustomerDharaRateFields = () => (
    <Grid container spacing={2} sx={{ mt: 0 }}>
      <Grid item xs={12} sm={6}>
        <TextField
          margin="normal"
          fullWidth
          id="rateOfInterest"
          label="Rate of Interest (% p.a.)"
          name="rateOfInterest"
          type="number"
          inputProps={{ min: 0, max: 100, step: 0.01 }}
          value={ledgerForm.rateOfInterest}
          onChange={handleInputChange}
          helperText="Defaults from Gold article in Article Master"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          margin="normal"
          required
          fullWidth
          select
          id="dhara"
          label="Dhara"
          name="dhara"
          value={ledgerForm.dhara || '30_day'}
          onChange={handleInputChange}
          SelectProps={{ native: true }}
        >
          {DHARA_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </TextField>
      </Grid>
    </Grid>
  );

  const renderCustomerNameRow = () => (
    <>
      <TextField
        margin="normal"
        required
        fullWidth
        id="firstName"
        label="First Name"
        name="firstName"
        value={ledgerForm.firstName}
        onChange={handleInputChange}
      />
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          margin="normal"
          select
          sx={{ width: 120, flex: '0 0 120px' }}
          id="relation"
          label="Relation"
          value={String(ledgerForm.relation || '').toLowerCase() === 'f/o' ? '' : (ledgerForm.relation || '')}
          onChange={(e) => {
            const relation = e.target.value;
            setLedgerForm((prev) => ({
              ...prev,
              relation
            }));
          }}
          SelectProps={{ native: true }}
        >
          <option value="">Select</option>
          <option value="d/o">D/O</option>
          <option value="s/o">S/O</option>
          <option value="c/o">C/O</option>
          <option value="w/o">W/O</option>
        </TextField>
        <TextField
          margin="normal"
          sx={{ flex: '1 1 120px', minWidth: 100 }}
          id="relationName"
          label="Name"
          value={ledgerForm.relationName || ''}
          onChange={(e) => setLedgerForm((prev) => ({ ...prev, relationName: e.target.value.toUpperCase() }))}
          placeholder="e.g. manish"
        />
        <TextField
          margin="normal"
          required
          sx={{ flex: '1 1 160px', minWidth: 120 }}
          id="lastName"
          label="Last Name"
          name="lastName"
          value={ledgerForm.lastName}
          onChange={handleInputChange}
        />
      </Box>
    </>
  );

  if (isModal) {
    return (
      <Box
        component="form"
        onSubmit={handleCreateLedger}
        noValidate
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, minmax(0, 1fr))' },
          columnGap: 1.25,
          rowGap: 0.75,
          alignItems: 'start',
          pt: 0.5,
          '& > .MuiTextField-root:first-of-type': { display: 'none' },
          '& .MuiFormControl-root': { marginTop: '8px', marginBottom: 0, minWidth: 0 },
          '& .MuiFormHelperText-root': { display: 'none' },
          '& .MuiInputLabel-root': {
            zIndex: 1,
            pointerEvents: 'none',
            fontSize: 13,
            backgroundColor: '#f8fafc',
            paddingLeft: '4px',
            paddingRight: '4px',
          },
          '& .MuiOutlinedInput-root': { background: '#fff', minHeight: 40 },
          '& .MuiOutlinedInput-input': { fontSize: 13, paddingTop: '8.5px', paddingBottom: '8.5px' },
        }}
      >
        <TextField
          margin="normal"
          select
          required
          fullWidth
          id="type"
          label="Type"
          name="type"
          value={ledgerForm.type}
          onChange={handleInputChange}
          SelectProps={{ native: true }}
        >
          {['Customer', 'Supplier', 'General'].map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </TextField>
        {/* Group Code - Required for General, Optional for Supplier */}
        {(ledgerForm.type === 'General' || ledgerForm.type === 'Supplier') && (
          <TextField
            margin="normal"
            select
            required={ledgerForm.type === 'General'}
            fullWidth
            id="groupCode"
            label="Group Code"
            name="groupCode"
            value={ledgerForm.groupCode}
            onChange={handleInputChange}
            SelectProps={{ native: true }}
          >
            <option value="">Select Group Code</option>
            {sortedGroupCodes.map((groupCode) => (
              <option key={groupCode._id} value={groupCode.code}>
                {groupCode.code} - {groupCode.name}
              </option>
            ))}
          </TextField>
        )}
        
        {/* Customer Fields */}
        {ledgerForm.type === 'Customer' && (
          <>
            <Box sx={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'minmax(0,1.2fr) minmax(0,1.2fr) 120px minmax(0,1fr)', gap: 1.25 }}>
              <TextField
                size="small"
                required
                fullWidth
                label="First Name"
                name="firstName"
                value={ledgerForm.firstName}
                onChange={handleInputChange}
              />
              <TextField
                size="small"
                required
                fullWidth
                label="Last Name"
                name="lastName"
                value={ledgerForm.lastName}
                onChange={handleInputChange}
              />
              <TextField
                size="small"
                select
                fullWidth
                label="Relation"
                value={String(ledgerForm.relation || '').toLowerCase() === 'f/o' ? '' : (ledgerForm.relation || '')}
                onChange={(e) => {
                  const relation = e.target.value;
                  setLedgerForm((prev) => ({
                    ...prev,
                    relation
                  }));
                }}
                SelectProps={{ native: true }}
              >
                <option value="">Select</option>
                <option value="d/o">D/O</option>
                <option value="s/o">S/O</option>
                <option value="c/o">C/O</option>
                <option value="w/o">W/O</option>
              </TextField>
              <TextField
                size="small"
                fullWidth
                label="Name"
                value={ledgerForm.relationName || ''}
                onChange={(e) => setLedgerForm((prev) => ({ ...prev, relationName: e.target.value.toUpperCase() }))}
                placeholder="e.g. manish"
              />
            </Box>
            <TextField
              size="small"
              required
              fullWidth
              label="Address"
              name="address"
              value={ledgerForm.address}
              onChange={handleInputChange}
              sx={{ gridColumn: '1 / -1' }}
            />
            <TextField
              size="small"
              fullWidth
              label="Rate of Interest (% p.a.)"
              name="rateOfInterest"
              type="number"
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              value={ledgerForm.rateOfInterest}
              onChange={handleInputChange}
            />
            <TextField
              size="small"
              required
              select
              fullWidth
              label="Dhara"
              name="dhara"
              value={ledgerForm.dhara || '30_day'}
              onChange={handleInputChange}
              SelectProps={{ native: true }}
            >
              {DHARA_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </TextField>
          </>
        )}
        
        {/* Supplier and General Fields */}
        {(ledgerForm.type === 'Supplier' || ledgerForm.type === 'General') && (
          <>
            <TextField
              margin="normal"
              required
              fullWidth
              id="firstName"
              label="Ledger Name"
              name="firstName"
              value={ledgerForm.firstName}
              onChange={handleInputChange}
              placeholder="e.g., Interest Income, Electricity Expense, Bank Account"

            />
            <TextField
              margin="normal"
              fullWidth
              multiline
              rows={3}
              id="address"
              label="Description"
              name="address"
              value={ledgerForm.address}
              onChange={handleInputChange}
              placeholder="Enter description for this ledger..."

            />
          </>
        )}
        
        {/* Opening Balance - Only for General Ledger */}
        {ledgerForm.type === 'General' && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                id="openingBalanceType"
                label="Opening Balance Type"
                name="openingBalanceType"
                value={ledgerForm.openingBalanceType}
                onChange={handleInputChange}
                SelectProps={{ native: true }}
              >
                <option value="">-- Select --</option>
                <option value="Debit">Debit</option>
                <option value="Credit">Credit</option>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                id="openingBalance"
                label="Opening Balance Amount"
                name="openingBalance"
                type="number"
                value={ledgerForm.openingBalance}
                onChange={handleInputChange}
                placeholder="0.00"
              />
            </Grid>
          </Grid>
        )}
        {/* Customer-specific fields */}
        {ledgerForm.type === 'Customer' && (
          <>
            <Autocomplete
              margin="normal"
              fullWidth
              freeSolo
              openOnFocus={false}
              id="caste"
              options={[...sortedCastes.map((c) => c.name), '__ADD_NEW__']}
              getOptionLabel={(opt) => (opt === '__ADD_NEW__' ? '+ Add New Caste' : opt)}
              filterOptions={(opts, state) => {
                const filtered = opts.filter((o) => o !== '__ADD_NEW__' && (state.inputValue === '' || (o && o.toUpperCase().includes((state.inputValue || '').toUpperCase()))));
                return [...filtered, '__ADD_NEW__'];
              }}
              value={ledgerForm.caste || ''}
              inputValue={ledgerForm.caste ?? ''}
              onInputChange={(e, v) => setLedgerForm((prev) => ({ ...prev, caste: (v || '') }))}
              onChange={(e, val) => {
                if (val === '__ADD_NEW__') {
                  setAddCasteModalOpen(true);
                  return;
                }
                setLedgerForm((prev) => ({ ...prev, caste: (val || '') }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Caste"
                  required
                  margin="normal"
                  inputProps={{
                    ...params.inputProps
                  }}
                />
              )}
              renderOption={(props, option) =>
                option === '__ADD_NEW__' ? (
                  <li
                    {...props}
                    key="__ADD_NEW__"
                    onClick={() => setAddCasteModalOpen(true)}
                    style={{ color: 'var(--mui-palette-primary-main)', fontWeight: 'bold' }}
                  >
                    <AddIcon sx={{ mr: 1, fontSize: 'small' }} />
                    Add New Caste
                  </li>
                ) : (
                  <li {...props} key={option}>
                    {option}
                  </li>
                )
              }
              ListboxProps={{
                style: { maxHeight: 280 }
              }}
            />
            <Autocomplete
              freeSolo
              openOnFocus={false}
              options={occupationSuggestions}
              value={ledgerForm.occupation || ''}
              inputValue={ledgerForm.occupation ?? ''}
              onInputChange={(e, v) => setLedgerForm((prev) => ({ ...prev, occupation: (v || '') }))}
              onChange={(e, val) => setLedgerForm((prev) => ({ ...prev, occupation: (typeof val === 'string' ? val : val || '') }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Occupation"
                  margin="normal"
                  fullWidth
                  inputProps={{
                    ...params.inputProps
                  }}
                />
              )}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="mobileNo"
              label="Mobile Number"
              name="mobileNo"
              value={ledgerForm.mobileNo}
              onChange={handleInputChange}
            />
            <FormControl size="small" sx={{ gridColumn: '1 / -1', maxWidth: 460 }}>
              <InputLabel id="modal-id-label">ID</InputLabel>
              <Select
                labelId="modal-id-label"
                multiple
                value={modalIdPicks}
                onChange={handleModalIdPicks}
                input={<OutlinedInput label="ID" />}
                renderValue={(selected) => (
                  selected.length
                    ? MODAL_ID_OPTIONS.filter((opt) => selected.includes(opt.value)).map((opt) => opt.label).join(', ')
                    : ''
                )}
                MenuProps={{ sx: { zIndex: 100000 }, PaperProps: { sx: { maxHeight: 320 } } }}
              >
                {MODAL_ID_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Checkbox size="small" checked={modalIdPicks.indexOf(opt.value) > -1} />
                    <ListItemText primary={opt.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {MODAL_ID_OPTIONS.filter((opt) => modalIdPicks.includes(opt.value)).map((opt) => (
              <Box key={opt.value} sx={{ gridColumn: '1 / -1', display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Box sx={{ width: 120, fontSize: 12, fontWeight: 700, color: '#334155', flex: '0 0 120px' }}>
                  {opt.label}
                </Box>
                {opt.numberKey ? (
                  <TextField
                    size="small"
                    label={opt.numberLabel}
                    value={idNumbers[opt.numberKey] || ''}
                    onChange={handleIdNumberChange(opt.numberKey)}
                    sx={{ width: 200, flex: '0 0 200px' }}
                  />
                ) : null}
                {opt.value === 'other' ? (
                  <>
                    <TextField size="small" label="Other ID Name" value={otherIdName} onChange={handleOtherIdNameChange} sx={{ width: 160 }} />
                    <TextField size="small" label="Other ID Number" value={otherIdNumber} onChange={handleOtherIdNumberChange} sx={{ width: 160 }} />
                  </>
                ) : null}
                {opt.photo ? (
                  <PhotoUploadComponent
                    key={opt.photo}
                    inline
                    photoType={opt.photo}
                    onPhotoCapture={handlePhotoCapture}
                    onFileUpload={handleFileUpload}
                    existingPhotos={ledgerForm.photos}
                  />
                ) : null}
              </Box>
            ))}
            <PlaceSearchField
              id="village"
              label="Village"
              value={ledgerForm.village}
              options={uniqueVillagesSorted}
              allowAddNew
              onAddNew={() => setAddVillageModalOpen(true)}
              onPick={(val) => setLedgerForm((prev) => ({ ...prev, village: val }))}
            />
            <PlaceSearchField
              id="atPost"
              label="At Post"
              value={ledgerForm.atPost}
              options={uniqueAtPostsSorted}
              onPick={(val) => setLedgerForm((prev) => ({ ...prev, atPost: val }))}
            />
          </>
        )}
        
        {/* Mobile Number for Supplier and General (optional) */}
        {(ledgerForm.type === 'Supplier' || ledgerForm.type === 'General') && (
          <TextField
            margin="normal"
            fullWidth
            id="mobileNo"
            label="Mobile Number (Optional)"
            name="mobileNo"
            value={ledgerForm.mobileNo}
            onChange={handleInputChange}
          />
        )}
        <PlaceSearchField
          id="taluka"
          label="Taluka"
          value={ledgerForm.taluka}
          options={uniqueTalukasSorted}
          onPick={(val) => setLedgerForm((prev) => ({ ...prev, taluka: val }))}
        />
        <PlaceSearchField
          id="district"
          label="District"
          value={ledgerForm.district}
          options={uniqueDistrictsSorted}
          onPick={(val) => setLedgerForm((prev) => ({ ...prev, district: val }))}
        />

        <Box sx={{ gridColumn: '1 / -1', mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            type="submit"
            disabled={createLoading}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              borderRadius: 2,
              px: 2.5,
              background: 'linear-gradient(180deg, #1e3a5f, #0b1220)',
              '&:hover': { background: 'linear-gradient(180deg, #2a4d78, #122038)' }
            }}
          >
            {createLoading ? <CircularProgress size={18} sx={{ color: '#f5e6c8' }} /> : 'Save Customer'}
          </Button>
        </Box>
        
        {/* Add New Modals */}
        <Dialog 
          open={addCasteModalOpen} 
          onClose={() => setAddCasteModalOpen(false)} 
          maxWidth="sm" 
          fullWidth
          disableRestoreFocus
          keepMounted={false}
        >
          <DialogTitle>Add New Caste</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Caste Name"
              type="text"
              fullWidth
              variant="outlined"
              value={newCasteForm.name}
              onChange={(e) => setNewCasteForm({ name: e.target.value })}
              sx={{ mt: 2 }}
              inputProps={{
                'aria-label': 'Caste name input'
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddCasteModalOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleAddCaste} 
              variant="contained" 
              disabled={casteLoading}
            >
              {casteLoading ? <CircularProgress size={20} /> : 'Add Caste'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog 
          open={addVillageModalOpen} 
          onClose={() => setAddVillageModalOpen(false)} 
          maxWidth="sm" 
          fullWidth
          disableRestoreFocus
          keepMounted={false}
        >
          <DialogTitle>Add New Village</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Village"
              type="text"
              fullWidth
              variant="outlined"
              value={newVillageForm.village}
              onChange={(e) => setNewVillageForm(prev => ({ ...prev, village: e.target.value }))}
              sx={{ mt: 2 }}

            />
            <TextField
              margin="dense"
              label="At Post"
              type="text"
              fullWidth
              variant="outlined"
              value={newVillageForm.atPost}
              onChange={(e) => setNewVillageForm(prev => ({ ...prev, atPost: e.target.value }))}

            />
            <TextField
              margin="dense"
              label="Taluka"
              type="text"
              fullWidth
              variant="outlined"
              value={newVillageForm.taluka}
              onChange={(e) => setNewVillageForm(prev => ({ ...prev, taluka: e.target.value }))}

            />
            <TextField
              margin="dense"
              label="District"
              type="text"
              fullWidth
              variant="outlined"
              value={newVillageForm.district}
              onChange={(e) => setNewVillageForm(prev => ({ ...prev, district: e.target.value }))}

            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              console.log('❌ Village Modal Cancel clicked');
              setAddVillageModalOpen(false);
            }}>Cancel</Button>
            <Button 
              onClick={handleAddVillage} 
              variant="contained" 
              disabled={villageLoading}
            >
              {villageLoading ? <CircularProgress size={20} /> : 'Add Village'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }



  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <MasterPageShell title="Ledgers" subtitle="Master · Accounts">
        <Grid container spacing={3}>
          {/* Create/Edit Ledger Form */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={masterPaperSx}>
              <Typography variant="h6" gutterBottom>
                {isEditing ? 'Edit Ledger' : 'Create Ledger'}
              </Typography>
              <Box component="form" onSubmit={handleCreateLedger} noValidate>
                <TextField
                  margin="normal"
                  select
                  required
                  fullWidth
                  id="type"
                  label="Type"
                  name="type"
                  value={ledgerForm.type}
                  onChange={handleInputChange}
                  SelectProps={{ native: true }}
                >
                  {['Customer', 'Supplier', 'General'].map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </TextField>
                {/* Group Code - Required for General, Optional for Supplier */}
                {(ledgerForm.type === 'General' || ledgerForm.type === 'Supplier') && (
                  <TextField
                    margin="normal"
                    select
                    required={ledgerForm.type === 'General'}
                    fullWidth
                    id="groupCode"
                    label="Group Code"
                    name="groupCode"
                    value={ledgerForm.groupCode}
                    onChange={handleInputChange}
                    SelectProps={{ native: true }}
                  >
                    <option value="">Select Group Code</option>
                    {sortedGroupCodes.map((groupCode) => (
                      <option key={groupCode._id} value={groupCode.code}>
                        {groupCode.code} - {groupCode.name}
                      </option>
                    ))}
                  </TextField>
                )}
                
                {/* Customer Fields */}
                {ledgerForm.type === 'Customer' && (
                  <>
                    {renderCustomerNameRow()}
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      multiline
                      rows={3}
                      id="address"
                      label="Address"
                      name="address"
                      value={ledgerForm.address}
                      onChange={handleInputChange}

                    />
                    {renderCustomerDharaRateFields()}
                    <Autocomplete
                      margin="normal"
                      fullWidth
                      freeSolo
                      openOnFocus={false}
                      id="caste-edit"
                      options={[...sortedCastes.map((c) => c.name), '__ADD_NEW__']}
                      getOptionLabel={(opt) => (opt === '__ADD_NEW__' ? '+ Add New Caste' : opt)}
                      filterOptions={(opts, state) => {
                        const filtered = opts.filter((o) => o !== '__ADD_NEW__' && (state.inputValue === '' || (o && o.toUpperCase().includes((state.inputValue || '').toUpperCase()))));
                        return [...filtered, '__ADD_NEW__'];
                      }}
                      value={ledgerForm.caste || ''}
                      inputValue={ledgerForm.caste ?? ''}
                      onInputChange={(e, v) => setLedgerForm((prev) => ({ ...prev, caste: (v || '') }))}
                      onChange={(e, val) => {
                        if (val === '__ADD_NEW__') {
                          setAddCasteModalOpen(true);
                          return;
                        }
                        setLedgerForm((prev) => ({ ...prev, caste: (val || '') }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Caste"
                          required
                          margin="normal"
                          inputProps={{
                            ...params.inputProps
                          }}
                        />
                      )}
                      renderOption={(props, option) =>
                        option === '__ADD_NEW__' ? (
                          <li
                            {...props}
                            key="__ADD_NEW__"
                            onClick={() => setAddCasteModalOpen(true)}
                            style={{ color: 'var(--mui-palette-primary-main)', fontWeight: 'bold' }}
                          >
                            <AddIcon sx={{ mr: 1, fontSize: 'small' }} />
                            Add New Caste
                          </li>
                        ) : (
                          <li {...props} key={option}>
                            {option}
                          </li>
                        )
                      }
                      ListboxProps={{
                        style: { maxHeight: 280 }
                      }}
                    />
                    <Box sx={{ mt: 0.5 }}>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setAddCasteModalOpen(true)}
                        sx={{ color: 'primary.main', fontWeight: 'bold', textTransform: 'none' }}
                      >
                        Add New Caste
                      </Button>
                    </Box>
                    <Autocomplete
                      freeSolo
                      openOnFocus={false}
                      options={occupationSuggestions}
                      value={ledgerForm.occupation || ''}
                      inputValue={ledgerForm.occupation ?? ''}
                      onInputChange={(e, v) => setLedgerForm((prev) => ({ ...prev, occupation: (v || '') }))}
                      onChange={(e, val) => setLedgerForm((prev) => ({ ...prev, occupation: (typeof val === 'string' ? val : val || '') }))}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Occupation"
                          margin="normal"
                          fullWidth
                          inputProps={{
                            ...params.inputProps
                          }}
                        />
                      )}
                    />
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="mobileNo"
                      label="Mobile Number"
                      name="mobileNo"
                      value={ledgerForm.mobileNo}
                      onChange={handleInputChange}
                    />
                  </>
                )}
                
                {/* Supplier and General Fields */}
                {(ledgerForm.type === 'Supplier' || ledgerForm.type === 'General') && (
                  <>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="firstName"
                      label="Ledger Name"
                      name="firstName"
                      value={ledgerForm.firstName}
                      onChange={handleInputChange}
                      placeholder="e.g., Interest Income, Electricity Expense, Bank Account"

                    />
                    <TextField
                      margin="normal"
                      fullWidth
                      multiline
                      rows={3}
                      id="address"
                      label="Description"
                      name="address"
                      value={ledgerForm.address}
                      onChange={handleInputChange}
                      placeholder="Enter description for this ledger..."

                    />
                    <TextField
                      margin="normal"
                      fullWidth
                      id="mobileNo"
                      label="Mobile Number (Optional)"
                      name="mobileNo"
                      value={ledgerForm.mobileNo}
                      onChange={handleInputChange}
                    />
                    
                    {/* Opening Balance - Only for General Ledger */}
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={6}>
                        <TextField
                          select
                          fullWidth
                          id="openingBalanceType"
                          label="Opening Balance Type"
                          name="openingBalanceType"
                          value={ledgerForm.openingBalanceType}
                          onChange={handleInputChange}
                          SelectProps={{ native: true }}
                        >
                          <option value="">-- Select --</option>
                          <option value="Debit">Debit</option>
                          <option value="Credit">Credit</option>
                        </TextField>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          id="openingBalance"
                          label="Opening Balance Amount"
                          name="openingBalance"
                          type="number"
                          value={ledgerForm.openingBalance}
                          onChange={handleInputChange}
                          placeholder="0.00"
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
                
                {/* Customer-specific ID Types and Address Fields */}
                {ledgerForm.type === 'Customer' && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel id="id-type-label">ID Types</InputLabel>
                        <Select
                          labelId="id-type-label"
                          multiple
                          value={selectedIdTypes}
                          onChange={handleIdTypeChange}
                          input={<OutlinedInput label="ID Types" />}
                          renderValue={(selected) => selected.join(', ')}
                        >
                          {idTypeOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Checkbox checked={selectedIdTypes.indexOf(option.value) > -1} />
                              <ListItemText primary={option.label} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    {selectedIdTypes.includes('aadhar') && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Aadhar Card Number"
                          value={idNumbers.aadhar}
                          onChange={handleIdNumberChange('aadhar')}
                          margin="normal"

                        />
                      </Grid>
                    )}
                    {selectedIdTypes.includes('pan') && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Pancard Number"
                          value={idNumbers.pan}
                          onChange={handleIdNumberChange('pan')}
                          margin="normal"

                        />
                      </Grid>
                    )}
                    {selectedIdTypes.includes('voter') && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Voter ID Number"
                          value={idNumbers.voter}
                          onChange={handleIdNumberChange('voter')}
                          margin="normal"

                        />
                      </Grid>
                    )}
                    {selectedIdTypes.includes('driving') && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Driving Licence Number"
                          value={idNumbers.driving}
                          onChange={handleIdNumberChange('driving')}
                          margin="normal"

                        />
                      </Grid>
                    )}
                    {showOtherFields && (
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Other ID Name"
                            value={otherIdName}
                            onChange={handleOtherIdNameChange}
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Other ID Number"
                            value={otherIdNumber}
                            onChange={handleOtherIdNumberChange}
                            margin="normal"
                          />
                        </Grid>
                      </Grid>
                    )}
                    <PlaceSearchField
                      id="village-edit"
                      label="Village"
                      value={ledgerForm.village}
                      options={uniqueVillagesSorted}
                      allowAddNew
                      onAddNew={() => setAddVillageModalOpen(true)}
                      onPick={(val) => setLedgerForm((prev) => ({ ...prev, village: val }))}
                    />
                    <PlaceSearchField
                      id="atPost-edit"
                      label="At Post"
                      value={ledgerForm.atPost}
                      options={uniqueAtPostsSorted}
                      onPick={(val) => setLedgerForm((prev) => ({ ...prev, atPost: val }))}
                    />
                    <PlaceSearchField
                      id="taluka-edit"
                      label="Taluka"
                      value={ledgerForm.taluka}
                      options={uniqueTalukasSorted}
                      onPick={(val) => setLedgerForm((prev) => ({ ...prev, taluka: val }))}
                    />
                    <PlaceSearchField
                      id="district-edit"
                      label="District"
                      value={ledgerForm.district}
                      options={uniqueDistrictsSorted}
                      onPick={(val) => setLedgerForm((prev) => ({ ...prev, district: val }))}
                    />
                  </>
                )}
                <Box sx={{ width: '100%', mt: 2 }}>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs 
                      value={photoUploadTab} 
                      onChange={(e, newValue) => setPhotoUploadTab(newValue)}
                      variant="fullWidth"
                    >
                      <Tab label="Aadhar Card" />
                      <Tab label="PAN Card" />
                      <Tab label="Client Photo" />
                    </Tabs>
                  </Box>
                  
                  <TabPanel value={photoUploadTab} index={0}>
                    <PhotoUploadComponent 
                      photoType="aadharCard"
                      onPhotoCapture={handlePhotoCapture}
                      onFileUpload={handleFileUpload}
                      existingPhotos={ledgerForm.photos}
                    />
                  </TabPanel>
                  
                  <TabPanel value={photoUploadTab} index={1}>
                    <PhotoUploadComponent 
                      photoType="panCard"
                      onPhotoCapture={handlePhotoCapture}
                      onFileUpload={handleFileUpload}
                      existingPhotos={ledgerForm.photos}
                    />
                  </TabPanel>
                  
                  <TabPanel value={photoUploadTab} index={2}>
                    <PhotoUploadComponent 
                      photoType="clientPhoto"
                      onPhotoCapture={handlePhotoCapture}
                      onFileUpload={handleFileUpload}
                      existingPhotos={ledgerForm.photos}
                    />
                  </TabPanel>
                </Box>
               
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={createLoading}
                  sx={{ mt: 2 }}
                >
                  {isEditing ? 'Update Ledger' : 'Create Ledger'}
                </Button>
                {isEditing && (
                  <Button
                    fullWidth
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setIsEditing(false);
                      resetForm();
                    }}
                    sx={{ mt: 1 }}
                  >
                    Cancel
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Ledgers List */}
          <Grid item xs={12} md={8}>
            <TableContainer component={Paper} elevation={3}>
              <Box sx={{ p: 2, pb: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search ledgers"
                  placeholder="Type customer code or name (e.g. CAA001 or Ramesh)"
                  value={ledgerSearch}
                  onChange={(e) => setLedgerSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    )
                  }}
                />
              </Box>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Group Code</TableCell>
                    <TableCell>Opening Balance</TableCell>
                    <TableCell>Mobile</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ledgers.map((ledger) => (
                    <TableRow key={ledger._id}>
                      <TableCell>{ledger.code || 'N/A'}</TableCell>
                      <TableCell>
                        {ledger.type === 'Customer'
                          ? (
                            <>
                              {`${ledger.firstName || ''} ${ledger.lastName || ''}`.trim() || 'N/A'}
                              {ledger.relation && String(ledger.relation).toLowerCase() !== 'f/o'
                                ? (
                                  <Typography variant="body2" color="text.secondary">
                                    {`${String(ledger.relation).toUpperCase()} ${String(ledger.relationName || '').toUpperCase()}`.trim()}
                                  </Typography>
                                )
                                : null}
                            </>
                          )
                          : ledger.firstName || 'N/A'
                        }
                      </TableCell>
                      <TableCell>{ledger.type}</TableCell>
                      <TableCell>
                        {(ledger.type === 'General' || ledger.type === 'Supplier') 
                          ? (ledger.groupCode || 'N/A') 
                          : '-'
                        }
                      </TableCell>
                      <TableCell align="right">
                        {ledger.openingBalance ? 
                          `${ledger.openingBalanceType === 'Debit' ? 'DR' : 'CR'} ${ledger.openingBalance}` 
                          : '-'
                        }
                      </TableCell>
                      <TableCell>{ledger.mobileNo}</TableCell>
                      <TableCell align="right">
                        <IconButton 
                          color="primary" 
                          onClick={() => handleEditLedger(ledger)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          color="error" 
                          onClick={() => {
                            setLedgerToDelete(ledger);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={ledgerTotalDocs}
                page={ledgerPage}
                onPageChange={handleLedgerPageChange}
                rowsPerPage={ledgerRowsPerPage}
                onRowsPerPageChange={handleLedgerRowsPerPageChange}
                rowsPerPageOptions={[25, 50, 100, 200]}
                labelRowsPerPage="Rows per page:"
              />
            </TableContainer>
          </Grid>
        </Grid>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Delete Ledger</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this ledger?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button 
              color="error" 
              onClick={handleDeleteLedger}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success/Error Snackbars */}
        <Snackbar 
          open={createSuccess} 
          autoHideDuration={3000} 
          onClose={handleCloseSnackbar}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity="success" 
            sx={{ width: '100%' }}
          >
            Operation successful!
          </Alert>
        </Snackbar>

        <Snackbar 
          open={!!createError} 
          autoHideDuration={3000} 
          onClose={handleCloseSnackbar}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity="error" 
            sx={{ width: '100%' }}
          >
            {createError}
          </Alert>
        </Snackbar>

        {/* Add New Modals */}
        <Dialog 
          open={addCasteModalOpen} 
          onClose={() => setAddCasteModalOpen(false)} 
          maxWidth="sm" 
          fullWidth
          disableRestoreFocus
          keepMounted={false}
        >
          <DialogTitle>Add New Caste</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Caste Name"
              type="text"
              fullWidth
              variant="outlined"
              value={newCasteForm.name}
              onChange={(e) => setNewCasteForm({ name: e.target.value })}
              sx={{ mt: 2 }}
              inputProps={{
                'aria-label': 'Caste name input'
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddCasteModalOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleAddCaste} 
              variant="contained" 
              disabled={casteLoading}
            >
              {casteLoading ? <CircularProgress size={20} /> : 'Add Caste'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog 
          open={addVillageModalOpen} 
          onClose={() => setAddVillageModalOpen(false)} 
          maxWidth="sm" 
          fullWidth
          disableRestoreFocus
          keepMounted={false}
        >
          <DialogTitle>Add New Village</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Village"
              type="text"
              fullWidth
              variant="outlined"
              value={newVillageForm.village}
              onChange={(e) => setNewVillageForm(prev => ({ ...prev, village: e.target.value }))}
              sx={{ mt: 2 }}

            />
            <TextField
              margin="dense"
              label="At Post"
              type="text"
              fullWidth
              variant="outlined"
              value={newVillageForm.atPost}
              onChange={(e) => setNewVillageForm(prev => ({ ...prev, atPost: e.target.value }))}

            />
            <TextField
              margin="dense"
              label="Taluka"
              type="text"
              fullWidth
              variant="outlined"
              value={newVillageForm.taluka}
              onChange={(e) => setNewVillageForm(prev => ({ ...prev, taluka: e.target.value }))}

            />
            <TextField
              margin="dense"
              label="District"
              type="text"
              fullWidth
              variant="outlined"
              value={newVillageForm.district}
              onChange={(e) => setNewVillageForm(prev => ({ ...prev, district: e.target.value }))}

            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddVillageModalOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleAddVillage} 
              variant="contained" 
              disabled={villageLoading}
            >
              {villageLoading ? <CircularProgress size={20} /> : 'Add Village'}
            </Button>
          </DialogActions>
        </Dialog>
    </MasterPageShell>
  );
};

export default LedgerMaster;